import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  Alert,
  ThemePrepared,
  Button,
  Loader,
} from '@fluentui/react-northstar';
import { Toast } from '../toast/Toast';
import { Communication } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import { Ticket } from '../../interfaces/ticket.interface';
import { BulkEditTicketForm } from './BulkEditTicketForm';
import Styles from './BulkEditTicket.module.css';
import { platformService } from '../../services/platform.service';
import {
  BULK_EDIT_VALUES,
  CONTACT_ADMIN,
  DEFAULT_API_STATUS,
  DONE,
  ERROR,
  STATUS_GUID,
  LIFECYCLE_STATUS_GUID,
  REFRESH_SESSION,
  SOMETHING_WENT_WRONG,
  UNAUTHORIZED_ACCESS,
} from '../../utils/constants';
import {
  ListContext,
  ListContextType,
} from '../../../tikit/List/ListContextProvider';
import { CustomViewContext } from '../../../tikit/toolbar/CustomViewContextProvider';
import { AppStateContext } from '../../../AppState';
import { getStatusIdByGuid } from '../../../tikit/ticketHelper';
import { useTranslation } from 'react-i18next';
import { ResolutionNoteDialog } from '../TicketResolutionNoteComponents/ResolutionNoteDialog';
import { TikitConfigState } from '../../../config/TikitConfiguration';
import { IResolution } from '../TicketResolutionNoteComponents/ResolutionHelper';
import { searchUsers } from '../../cache/PlatformUserCache';

interface Props {
  globalTheme: ThemePrepared<any>;
  open: boolean;
  onClose: (value: boolean) => void;
  tickets: Ticket[];
  onSuccessfullyUpdate: (listContext: ListContextType, customViewStates: CustomViewStatesType) => void;
  listFeatureStates: ListFeatureStates;
}

const api = new platformService();

export const BulkEditTicketDialog = ({
  open,
  onClose,
  tickets,
  onSuccessfullyUpdate,
  listFeatureStates
}: Props) => {
  const {
    ticketStatus
  } = useContext(AppStateContext);
  const { t } = useTranslation();
  const [showEditTicketsDialog, setShowEditTicketsDialog] = useState(false);
  const { listStates, setListStates } = useContext(ListContext);
  const { customViewStates } = useContext(CustomViewContext);

  const [ticketConfig, setTicketConfig] = useState<TikitConfiguration>(TikitConfigState);
  const [showResolvedDialog, setShowResolvedDialog] = useState(false);
  const [updatedResolution, setUpdatedResolution] = useState<ResolutionDetail>(null);
  const [resolveStatusId, setResolveStatusId] = useState<number>(0);
  const [showProcessingDialog, setShowProccessingDialog] = useState<boolean>(false);
  const [processingMessage, setProcessingMessage] = useState<string>('');

  const [ticketData, setTicketData] =
    useState<BulkTicketEntity>(BULK_EDIT_VALUES);
  const [apiStatus, setAPIStatus] = useState<APIStatus>(
    DEFAULT_API_STATUS as APIStatus
  );
  const [hasError, setHasError] = useState({
    error: false,
    msg: '',
  });

  const [alert, setAlert] = useState({
    title: '',
    show: false,
    attributes: {},
  });


  useEffect(() => {
    setResolveStatusId(getStatusIdByGuid(ticketStatus, STATUS_GUID.RESOLVED));
  }, [ticketStatus]);

  useEffect(() => {
    if (listFeatureStates?.resolutionNoteConfig) setTicketConfig(listFeatureStates?.resolutionNoteConfig);
  }, [listFeatureStates]);

  useEffect(() => {
    setShowEditTicketsDialog(open);
  }, [open]);


  const onOpen = () => {
    setAlert({ title: '', show: false, attributes: {} });
    setTicketData(null);
  };

  const updateAPIStatus = (newState: Partial<APIStatus>) => {
    setAPIStatus({ ...apiStatus, ...newState });
  };

  const skeletonLoading = (Id: number) => {
    setListStates((prev: any) => {
      // change current reference to heap to make sure state was changed.
      const _tickets: Ticket[] = prev.tickets.map((ticket: Ticket) => {
        if (ticket.Id === Id) {
          return {
            ...ticket,
            Title: 'temp_ticket',
          };
        }
        return ticket;
      });

      return {
        ...prev,
        tickets: _tickets,
      };
    });
  };

  const buildTicketData = () => {
    return Object.keys(ticketData).reduce((acc, key) => {
      if(key == "CustomPropertiesAnswers"){
        const cpAnswerJson: any = {};
        ticketData.CustomPropertiesAnswers.forEach(cp => {
          if(cp.editValue?.required && (cp.editValue?.value ?? "").length > 0)
            cpAnswerJson[cp.key] = cp.editValue.value;
        });
        acc["CustomPropertiesAnswerJson"] = JSON.stringify(cpAnswerJson);
      }
      else if (ticketData[key].required) {
        acc[key] = ticketData[key].value;
      } 
      return acc;
    }, {} as Ticket);
  }

  const submit = async () => {
    let ticket = buildTicketData();
    let status = (ticket["StatusId"]) ? Number(ticket["StatusId"]) : 0;
    if (listFeatureStates.userResolutionNote && status == resolveStatusId)
      setShowResolvedDialog(true);
    else 
      await saveData(ticket, false);
  };


  const saveData = async (ticket: Ticket, fromResolveDialog: boolean) => {

    setHasError({
        error: false,
        msg: "",
      });
    const hasTicketValue = Object.keys(ticket).length > 0;

    if (!hasTicketValue) {
      return setHasError({
        error: true,
        msg: t('ticket-details.edit-ticket-view.please-enter-at-least-one-field-to-update'),
      });
    }

    const isValid = Object.keys(ticket).every((key) => ticket[key] !== null);

    if (!isValid) {
      return setHasError({
        error: true,
        msg: t('ticket-details.edit-ticket-view.please-enter-values-to-required-fields-to-update'),
      });
    }
    
    const lifecycleTicketsId = [];
    const ticketsId = [];
    const closeStatusId = getStatusIdByGuid(ticketStatus, STATUS_GUID.CLOSED);
    const ticketStatusId = (ticket["StatusId"]) ? Number(ticket["StatusId"]) : 0;
    tickets.forEach(item => {
      skeletonLoading(item.Id);
      const lifecycleIsActive = (item.TicketLifecycle?.Status ?? false) && [LIFECYCLE_STATUS_GUID.NOT_STARTED, LIFECYCLE_STATUS_GUID.IN_PROGRESS].indexOf(item.TicketLifecycle.Status.Guid) >= 0;
      if (listFeatureStates.useTicketLifecycle && lifecycleIsActive && (ticketStatusId == closeStatusId || ticketStatusId == resolveStatusId)) {
        lifecycleTicketsId.push(item.Id);
      } else
        ticketsId.push(item.Id);
    });


    if (ticketData.RequesterId.required) {
      const { Id } = await searchUsers(ticketData.RequesterId.value as PlatformUser);
      ticket = {
        ...ticket,
        RequesterId: Id,
      };
    }

    await doBulkEdit(ticketsId, lifecycleTicketsId, ticket, fromResolveDialog);
  }

  const doBulkEdit = async (ticketsId: number[], lifecycleTicketsId: number[], ticket: Ticket, fromResolveDialog: boolean) => {

    const updatedTickets = ticketsId.map((Id) => ({
      Id: Id,
      ...ticket,
    }));

    updatedTickets.forEach((ticketItem) => {
      if (ticketItem.TeamId && ticketItem.TeamId.toString() === '-1')
        ticketItem.TeamId = null;
      if (ticketItem.SupportGroupId && ticketItem.SupportGroupId.toString() === '-1')
        ticketItem.SupportGroupId = null;
      if (ticketItem.AssigneeId && ticketItem.AssigneeId.toString() === '-1')
        ticketItem.AssigneeId = null;
    });

    try {

      if (ticketsId.length > 0) {
        setShowProccessingDialog(true);
        setProcessingMessage(t('ticket-details.edit-ticket-view.processing-items', { processed: 0, total: ticketsId.length }));
        for (let i = 0; i < ticketsId.length; i += 5) {
          const chunk = ticketsId.slice(i, i + 5);
          await api.bulkEditTickets(chunk, JSON.stringify(updatedTickets[0]), fromResolveDialog ? JSON.stringify(updatedResolution) : "");
          await new Promise(res => setTimeout(res, 1000));
          setProcessingMessage(t('ticket-details.edit-ticket-view.processing-items', { processed: i, total: ticketsId.length }));
        }

        setShowEditTicketsDialog(false);
        updatedTickets.forEach((item) => { Toast.success(t('ticket-details.edit-ticket-view.ticket-updated-successfully', { ticketId: item.Id })); });
        setAPIStatus({ ...apiStatus, status: DONE });
        setHasError({ error: false, msg: '', });
      }

      lifecycleTicketsId.forEach((id) => {
        Toast.error(`Ticket #${id} has an active lifecycle and cannot be moved to resolved or closed.`);
      });

      setTicketData(BULK_EDIT_VALUES);
      onSuccessfullyUpdate({ listStates, setListStates }, customViewStates);
    } catch (error: any) {
      setHasError({ error: false, msg: '', });
      setTicketData(BULK_EDIT_VALUES);
      onSuccessfullyUpdate({ listStates, setListStates }, customViewStates);
      updateAPIStatus({ status: ERROR, errCode: error.response?.status ?? 500, });
      Toast.error((error.response) ? t('ticket-details.edit-ticket-view.failed-to-updated-the-tickets') : t('ticket-details.edit-ticket-view.update-request-timed-out'));
    } finally {
      onClose(false);
      setShowProccessingDialog(false);
    }
  };

  const errConfig = {
    fields: {
      title: apiStatus.errCode === 401 ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN,
    },
  };


  return (
    <Dialog
      className={`responsive-dialog ${Styles.bulkEditDialog}`}
      style={{ width: 600 }}
      closeOnOutsideClick={false}
      cancelButton={{ content: t('ticket-details.edit-ticket-view.cancel'), styles: showProcessingDialog ? { display: 'none' } : {} }}
      confirmButton={{
        content: t('ticket-details.edit-ticket-view.update'),
        primary: true,
        styles: showProcessingDialog ? { display: 'none' } : {}
      }}
      onConfirm={submit}
      onCancel={() => {
        setShowEditTicketsDialog(false);
        setTicketData(BULK_EDIT_VALUES);
        setHasError({
          error: false,
          msg: '',
        });
        onClose(false);
      }}
      open={showEditTicketsDialog}
      onOpen={onOpen}
      content={
        <div className='relative'>
          {apiStatus.status === ERROR ? (
            <>
              <div style={{ height: '100%' }}>
                <Communication {...errConfig} />
                {apiStatus.errCode === 401 && (
                  <Button
                    content={REFRESH_SESSION}
                    primary
                    onClick={(_e) => {
                      Providers.globalProvider.login();
                    }}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              {hasError.error && (
                <Alert content={hasError.msg} danger visible />
              )}
              <ResolutionNoteDialog
                resolutionProp={{ ticketData: {}, resolutionNoteConfig: ticketConfig } as IResolution}
                setShowDialog={setShowResolvedDialog}
                showDialog={showResolvedDialog}
                setUpdatedResolution={setUpdatedResolution}
                disableDraftKB={true}
                isBulkEdit={true}
                confirmSave={async () => {
                  await saveData(buildTicketData(), true);
                }}
              />
              {showProcessingDialog && <Loader label={processingMessage} />}
              {!showProcessingDialog && <BulkEditTicketForm ticketData={ticketData} setTicketData={setTicketData} />}
            </>
          )}
        </div>
      }
      header={{
        content: (
          <div style={{ width: '100%' }}>
            <h3 style={{ marginTop: 0 }}>{t('ticket-details.edit-ticket-view.update-tickets')}</h3>
            {alert.show && (
              <Alert
                fitted
                {...alert.attributes}
                content={alert.title}
                style={{
                  fontSize: 12,
                  width: '100%',
                }}
              />
            )}
          </div>
        ),
        style: { width: '100%' },
      }}
    />
  );
};
